import React, {useState, useEffect, useRef, useContext} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { TextField, Button, Box, Typography, Autocomplete, CircularProgress, Grid } from '@mui/material';
import {fetchCities, fetchCouriers, fetchDistricts, fetchPackages, fetchProducts, submitDrop} from "../../api.js";
import {UserContext} from "../../shared/Greet.jsx";

const Add = () => {

  const { isAuth, user } = useContext(UserContext);

  const { control, handleSubmit, setValue, reset } = useForm();
  const [cities, setCities] = useState([]);
  const [products, setProducts] = useState({});
  const [districts, setDistricts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [packagesLoading, setPackagesLoading] = useState(false);

  const [isMagnet, setIsMagnet] = useState(false)
  const [isPackaged, setIsPackaged] = useState(false)

  // Массив блоков комментарий + фотки
  const [commentBlocks, setCommentBlocks] = useState([
    { id: 0, comment: '', file1: null, file2: null }
  ])

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false)

  // Refs для очистки файловых input'ов
  const fileInputRefs = useRef({});

  useEffect(() => {
    if(isAuth){
      fetchCities().then(setCities).catch((error) => console.error(error));
      fetchProducts().then(setProducts).catch((error) => console.error(error));
    }
  }, [isAuth]);

  useEffect(() => {
    if(isAuth && user.user_id){
      if(user.isAdmin || user.isOperator){
        fetchCouriers().then((couriers) => {
          setCouriers(couriers)
        })
      }
    }
  }, [isAuth, user]);

  // Функции для управления блоками комментарий + фотки
  const addCommentBlock = () => {
    // Копируем комментарий из предыдущего блока
    const prevBlock = commentBlocks[commentBlocks.length - 1];
    const newBlock = {
      id: commentBlocks.length,
      comment: prevBlock ? prevBlock.comment : '',
      file1: null,
      file2: null
    };
    setCommentBlocks([...commentBlocks, newBlock]);
  };

  const removeCommentBlock = (blockId) => {
    if (commentBlocks.length > 1) {
      setCommentBlocks(commentBlocks.filter(block => block.id !== blockId));
    }
  };

  const updateBlockComment = (blockId, comment) => {
    setCommentBlocks(commentBlocks.map(block => 
      block.id === blockId ? { ...block, comment } : block
    ));
  };

  const updateBlockFile = (blockId, fileType, file) => {
    setCommentBlocks(commentBlocks.map(block => 
      block.id === blockId ? { ...block, [fileType]: file } : block
    ));
  };

  // Функция для очистки всех файловых input'ов
  const clearAllFileInputs = () => {
    Object.values(fileInputRefs.current).forEach(ref => {
      if (ref) {
        ref.value = '';
      }
    });
  };

  // Функция для обработки множественного выбора файлов
  const handleMultipleFileChange = (blockId, event) => {
    const files = Array.from(event.target.files);
    
    // Ограничиваем максимум 2 файла
    if (files.length > 2) {
      alert('Можно выбрать максимум 2 файла');
      event.target.value = '';
      return;
    }

    // Обновляем состояние блока
    setCommentBlocks(commentBlocks.map(block => {
      if (block.id === blockId) {
        return {
          ...block,
          file1: files[0] || null,
          file2: files[1] || null
        };
      }
      return block;
    }));
  };

  const onSubmit = async (data) => {
    setLoading(true)

    if (!data.city || !data.district || !data.package) {
      setErrorMessage('Ошибка: заполните поля');
      setSuccessMessage('');
      setLoading(false)
      return;
    }

    // Проверяем, что все блоки заполнены
    const emptyBlocks = commentBlocks.filter(block => 
      !block.comment.trim() || (!block.file1 && !block.file2)
    );
    
    if (emptyBlocks.length > 0) {
      setErrorMessage('Ошибка: заполните комментарии и добавьте файлы для всех блоков');
      setSuccessMessage('');
      setLoading(false)
      return;
    }

    const baseDropData = {
      cityId: data.city.id,
      districtId: data.district.id,
      packageId: data.package.id,
      isPackaged,
      dropType: isMagnet ? 'magnet' : 'regular'
    };

    if((user.isAdmin || user.isOperator) && data.courier){
      baseDropData.courierId = data.courier
    }

    const formData = new FormData();
    const isMultipleBlocks = commentBlocks.length > 1;

    if (isMultipleBlocks) {
      // Множественные блоки: отправляем массив дропов
      const dropsArray = commentBlocks.map(block => ({
        ...baseDropData,
        comment: block.comment
      }));
      formData.append('drop', JSON.stringify(dropsArray));

      // Фото отправляются с индексом для каждого блока
      commentBlocks.forEach((block, index) => {
        if(block.file1){
          formData.append(`photos_${index}`, block.file1);
        }
        if(block.file2){
          formData.append(`photos_${index}`, block.file2);
        }
      });
    } else {
      // Одиночный блок: отправляем как объект
      const singleDrop = {
        ...baseDropData,
        comment: commentBlocks[0].comment
      };
      formData.append('drop', JSON.stringify(singleDrop));

      // Фото отправляются под ключом photos
      if(commentBlocks[0].file1){
        formData.append('photos', commentBlocks[0].file1);
      }
      if(commentBlocks[0].file2){
        formData.append('photos', commentBlocks[0].file2);
      }
    }

    const result = await submitDrop(formData)

    if(result.success){
      setLoading(false)
      setSuccessMessage('Успех');

      setTimeout(() => {
        setSuccessMessage('');
      }, 4000)

      setErrorMessage('');
      setValue('comment', '');

      setValue('product', null);
      setValue('package', null);

      // Очищаем все блоки комментариев и файлов
      setCommentBlocks([{ id: 0, comment: '', file1: null, file2: null }]);
      
      // Очищаем файловые input'ы
      clearAllFileInputs();
    }else{
      setLoading(false)
      const error = result.status === 503 ? "Не установлена оплата. Обратитесь к админу" : (result.error || 'Ошибка')
      setErrorMessage(error);
      setTimeout(() => {
        setErrorMessage("")
      }, 4000)
      setSuccessMessage('');
    }

  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ maxWidth: 600, margin: 'auto', padding: 2, bgColor: "fff" }}>
      <Grid container spacing={2}>

        <h2>Добавить Клад</h2>

        {
            (user.isAdmin || user.isOperator) && <Grid item xs={12}>
              <Controller
                  name="courier"
                  control={control}
                  render={({field}) => (
                      <Autocomplete
                          {...field}
                          options={couriers}
                          getOptionLabel={(option) => option.full_name || ''}
                          renderInput={(params) => <TextField {...params} label="Выбери курьера"/>}
                          value={couriers.find(courier => courier.user_id === field.value) || null}
                          onChange={(_, data) => {
                            field.onChange(data ? data.user_id : null); // Исправлено
                          }}
                      />
                  )}
              />
            </Grid>
        }

        <Grid item xs={12}>
          <Controller
              name="city"
              control={control}
              render={({field}) => (
                  <Autocomplete
                      {...field}
                      options={cities}
                      getOptionLabel={(option) => option.name || ''}
                      renderInput={(params) => <TextField {...params} label="Выбери город*"/>}
                      value={field.value || null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue('district', null);
                        setDistrictsLoading(true);
                        fetchDistricts(data.id).then((districts) => {
                          setDistricts(districts);
                          setDistrictsLoading(false);
                        }).catch((error) => {
                          console.error(error);
                          setDistrictsLoading(false);
                        });
                      }}
                  />
              )}
          />
        </Grid>
        <Grid item xs={12}>
          {districtsLoading ? <CircularProgress/> : (
              <Controller
                  name="district"
                  control={control}
                  render={({field}) => (
                      <Autocomplete
                          {...field}
                          options={districts}
                          getOptionLabel={(option) => option.district || ''}
                          renderInput={(params) => <TextField {...params} label="Выбери район*"/>}
                          value={field.value || null}
                          onChange={(_, data) => field.onChange(data)}
                      />
                  )}
              />
          )}
        </Grid>
        <Grid item xs={12}>
          <Controller
              name="product"
              control={control}
              render={({field}) => (
                  <Autocomplete
                      {...field}
                      options={Object.keys(products).map(key => ({id: key, ...products[key]}))}
                      getOptionLabel={(option) => option.short_description || ''}
                      renderInput={(params) => <TextField {...params} label="Выбери продукт*"/>}
                      value={field.value || null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue('package', null);
                        setPackagesLoading(true);
                        fetchPackages(data.id).then((packages) => {
                          setPackages(packages);
                          setPackagesLoading(false);
                        }).catch((error) => {
                          console.error(error);
                          setPackagesLoading(false);
                        });
                      }}
                  />
              )}
          />
        </Grid>
        <Grid item xs={12}>
          {packagesLoading ? <CircularProgress/> : (
              <Controller
                  name="package"
                  control={control}
                  render={({field}) => (
                      <Autocomplete
                          {...field}
                          options={packages}
                          getOptionLabel={(option) => option.name || option.quantity.toString() || ''}
                          renderInput={(params) => <TextField {...params} label="Выбери package*"/>}
                          value={field.value || null}
                          onChange={(_, data) => field.onChange(data)}
                      />
                  )}
              />
          )}
        </Grid>

        <Grid item xs={12}>
          <label>
            <input checked={isMagnet} onChange={(e) => setIsMagnet(e.target.checked)} type="checkbox" />
            <span>Магнит</span>
          </label>
        </Grid>

        <Grid item xs={12}>
          <label>
            <input checked={isPackaged} onChange={(e) => setIsPackaged(e.target.checked)} type="checkbox" />
            <span>Фасовка</span>
          </label>
        </Grid>

        {/* Динамические блоки комментарий + фотки */}
        {commentBlocks.map((block, index) => {
          const isMultiple = commentBlocks.length > 1;
          
          return (
            <div key={block.id} className={isMultiple ? "border-2 ml-4 border-gray-300 rounded p-4 flex flex-col my-4 w-full gap-2 border-solid" : "ml-4 flex flex-col my-4 w-full gap-2"}>
              {isMultiple && (
                <div className="flex justify-between items-center mb-2">
                  <h4>Клад {index + 1}</h4>
                  <Button 
                    size="small" 
                    color="error" 
                    onClick={() => removeCommentBlock(block.id)}
                  >
                    ✕ Удалить
                  </Button>
                </div>
              )}
              
              <Grid item xs={12}>
                <TextField
                  label="Введи комментарий*"
                  multiline
                  rows={3}
                  fullWidth
                  value={block.comment}
                  onChange={(e) => updateBlockComment(block.id, e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <div className="flex flex-col gap-2">
                  <div>
                    <label>Выберите фото (до 2х файлов):</label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      ref={el => fileInputRefs.current[`${block.id}_multiple`] = el}
                      onChange={(e) => handleMultipleFileChange(block.id, e)}
                    />
                    <div className="flex gap-2 mt-2">
                      {block.file1 && (
                        <img
                          src={URL.createObjectURL(block.file1)}
                          alt="Фото 1"
                          style={{width: "100px", height: "100px", objectFit: "cover"}}
                        />
                      )}
                      {block.file2 && (
                        <img
                          src={URL.createObjectURL(block.file2)}
                          alt="Фото 2"
                          style={{width: "100px", height: "100px", objectFit: "cover"}}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </Grid>
            </div>
          );
        })}

        {/* Кнопка добавления нового блока */}
        <Grid item xs={12}>
          <Button 
            variant="outlined" 
            onClick={addCommentBlock}
            fullWidth
            startIcon={<span>+</span>}
          >
            Добавить еще один дроп
          </Button>
        </Grid>


        <Grid item xs={12}>
          <Button disabled={loading} type="submit" variant="contained" color="primary" fullWidth>
            Отправить
          </Button>
          {
              loading && <div className="lds-dual-ring"></div>
          }
        </Grid>
        {successMessage && (
            <Grid item xs={12}>
              <Typography color="green">{successMessage}</Typography>
            </Grid>
        )}
        {errorMessage && (
            <Grid item xs={12}>
              <Typography color="red">{errorMessage}</Typography>
            </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Add;

